import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Search, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { useTeamsDataApi } from './useTeamsDataApi'

// Player search + roster browse. The same /players endpoint that
// tbtv-highlights will eventually call for entity resolution — exposed
// here so editorial can verify lookup behavior end-to-end.
export default function PlayersBrowser() {
  const api = useTeamsDataApi()
  const [orgs, setOrgs] = useState([])
  const [filters, setFilters] = useState({ search: '', org_id: '', sport: '', season: '' })
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [expanded, setExpanded] = useState(new Set())

  useEffect(() => {
    api.listOrgs().then(r => setOrgs(r?.orgs || [])).catch(() => setOrgs([]))
  }, [api])

  const search = useCallback(() => {
    setLoading(true); setErr(null)
    api.searchPlayers({ ...filters, limit: 200 })
      .then(r => setResult(r))
      .catch(e => setErr(e?.message || String(e)))
      .finally(() => setLoading(false))
  }, [api, filters])

  // Debounce search-as-you-type a little so we don't hammer D1 on every keystroke.
  useEffect(() => {
    const handle = setTimeout(search, 250)
    return () => clearTimeout(handle)
  }, [search])

  const sports = useMemo(() => Array.from(new Set(orgs.map(o => o.sport))).sort(), [orgs])

  function toggle(playerId) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(playerId)) next.delete(playerId); else next.add(playerId)
      return next
    })
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#020617', border: '1px solid #334155', borderRadius: 4, padding: '0 8px', minWidth: 240 }}>
          <Search size={11} style={{ color: '#888' }} />
          <input placeholder="Search by name (try mangled spellings)" value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            style={{ padding: '6px 0', background: 'transparent', border: 'none', color: '#dbe5f5', fontSize: 12, width: '100%' }} />
        </div>
        <select value={filters.org_id} onChange={e => setFilters({ ...filters, org_id: e.target.value })} style={inp}>
          <option value="">All orgs</option>
          {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.short_name || o.org_id}</option>)}
        </select>
        <select value={filters.sport} onChange={e => setFilters({ ...filters, sport: e.target.value })} style={inp}>
          <option value="">All sports</option>
          {sports.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input placeholder="season" value={filters.season} onChange={e => setFilters({ ...filters, season: e.target.value })} style={{ ...inp, width: 100 }} />
        {result && (
          <span style={{ color: '#888', fontSize: 12 }}>
            {result.match_count} match{result.match_count === 1 ? '' : 'es'}
            {result.normalized && result.normalized !== filters.search && (
              <em style={{ marginLeft: 6, color: '#7dd3fc' }}>normalized: "{result.normalized}"</em>
            )}
          </span>
        )}
      </div>

      {loading && <Loading />}
      {err && <div style={{ color: '#f87171' }}>{err}</div>}

      {!loading && !err && result && (
        <div style={{ border: '1px solid #1e293b', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#020617', color: '#94a3b8' }}>
                <th style={{ ...th, width: 24 }}></th>
                <th style={th}>Player</th>
                <th style={th}>Team</th>
                <th style={th}>Div</th>
                <th style={th}>#</th>
                <th style={th}>Pos</th>
                <th style={th}>Season</th>
                <th style={th}>Match</th>
              </tr>
            </thead>
            <tbody>
              {(result.players || []).map(p => (
                <Row key={p.player_id} player={p} api={api}
                  expanded={expanded.has(p.player_id)} onToggle={() => toggle(p.player_id)} />
              ))}
              {(result.players || []).length === 0 && (
                <tr><td colSpan={8} style={{ ...td, color: '#888', textAlign: 'center', padding: 16 }}>
                  {filters.search ? `No matches for "${filters.search}".` : 'Most-recently-seen players appear here.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Row({ player, api, expanded, onToggle }) {
  return (
    <>
      <tr style={{ borderTop: '1px solid #1e293b', cursor: 'pointer' }} onClick={onToggle}>
        <td style={{ ...td, textAlign: 'center' }}>{expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}</td>
        <td style={{ ...td, color: '#dbe5f5', fontWeight: 600 }}>{player.canonical_name}</td>
        <td style={td}>{player.team_display_name}</td>
        <td style={td}>{player.division || '—'}</td>
        <td style={{ ...td, fontFamily: 'monospace' }}>{player.jersey_number ?? '—'}</td>
        <td style={td}>{player.position || '—'}</td>
        <td style={td}>{player.season}</td>
        <td style={td}>
          {player.match_kind && (
            <span style={{
              fontSize: 10, padding: '1px 6px', borderRadius: 10,
              background: player.match_kind === 'exact' ? 'rgba(34,197,94,0.15)' : 'rgba(125,211,252,0.15)',
              color: player.match_kind === 'exact' ? '#86efac' : '#7dd3fc',
              fontFamily: 'monospace',
            }}>{player.match_kind}{player.match_score != null ? ` ${player.match_score.toFixed(2)}` : ''}</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: '#0d1622' }}>
          <td></td>
          <td colSpan={7} style={{ padding: '8px 10px' }}>
            <StatsPanel playerId={player.player_id} api={api} />
            <AliasesPanel playerId={player.player_id} api={api} />
            <div style={{ marginTop: 8, fontSize: 10, color: '#888', fontFamily: 'monospace' }}>
              player_id: {player.player_id}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// Renders per-season stats for a player. Calls getPlayer which now returns
// the joined `stats` array. Skater vs goalie rendered side-by-side with the
// columns that matter for each position.
function StatsPanel({ playerId, api }) {
  const [data, setData] = useState(null)

  useEffect(() => {
    let cancelled = false
    api.getPlayer(playerId)
      .then(r => { if (!cancelled) setData(r) })
      .catch(() => { if (!cancelled) setData({ stats: [] }) })
    return () => { cancelled = true }
  }, [api, playerId])

  if (data === null) return <Loading small />
  const stats = data.stats || []
  if (stats.length === 0) return (
    <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', marginBottom: 8 }}>
      No season stats uploaded for this player yet.
    </div>
  )

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>Season stats</div>
      {stats.map(s => (
        <div key={`${s.season}|${s.position_kind}`} style={{
          background: '#020617', border: '1px solid #1e293b', borderRadius: 4,
          padding: '6px 8px', marginBottom: 4, fontSize: 11,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ color: '#dbe5f5', fontWeight: 600 }}>{s.season}</span>
            <span style={{
              padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700,
              background: s.position_kind === 'goalie' ? 'rgba(244,114,182,0.15)' : 'rgba(125,211,252,0.15)',
              color: s.position_kind === 'goalie' ? '#f472b6' : '#7dd3fc',
            }}>{s.position_kind}</span>
            <div style={{ flex: 1 }} />
            <span style={{ color: '#666', fontSize: 10 }}>updated {s.updated_at}</span>
          </div>
          {s.position_kind === 'skater' ? (
            <StatGrid items={[
              ['GP', s.gp], ['G', s.goals], ['A', s.assists], ['P', s.points],
              ['PT/G', s.points_per_game?.toFixed?.(2) ?? s.points_per_game],
              ['PIM', s.pim], ['PPG', s.ppg], ['SHG', s.shg], ['GWG', s.gwg],
              ['SOG', s.sog], ['SH%', s.shot_pct?.toFixed?.(1) ?? s.shot_pct],
              ['+/-', s.plus_minus],
            ]} />
          ) : (
            <StatGrid items={[
              ['GP', s.gp], ['W', s.wins], ['L', s.losses], ['OTL', s.otl],
              ['SOW', s.shootout_wins], ['SOL', s.shootout_losses],
              ['GA', s.ga], ['GAA', s.gaa?.toFixed?.(2) ?? s.gaa],
              ['SV', s.saves],
              ['SV%', s.sv_pct != null ? (s.sv_pct < 1 ? (s.sv_pct * 100).toFixed(1) + '%' : s.sv_pct) : null],
              ['SO', s.shutouts], ['MIN', s.min_played],
            ]} />
          )}
          {s.raw_stats_json && (
            <details style={{ marginTop: 4 }}>
              <summary style={{ cursor: 'pointer', color: '#666', fontSize: 10 }}>raw scraped stats</summary>
              <pre style={{ margin: '2px 0 0', fontSize: 10, color: '#888' }}>{s.raw_stats_json}</pre>
            </details>
          )}
        </div>
      ))}
    </div>
  )
}

function StatGrid({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))', gap: 4 }}>
      {items.map(([label, value]) => (
        <div key={label} style={{
          background: '#0d1622', padding: '4px 6px', borderRadius: 3,
          display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0,
        }}>
          <span style={{ fontSize: 9, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
          <span style={{ fontSize: 12, color: value != null && value !== '' ? '#dbe5f5' : '#444', fontFamily: 'monospace' }}>
            {value != null && value !== '' ? value : '—'}
          </span>
        </div>
      ))}
    </div>
  )
}

function AliasesPanel({ playerId, api }) {
  const [aliases, setAliases] = useState(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({ alias_form: '', alias_kind: 'nickname', confidence: 1.0 })

  const refresh = useCallback(() => {
    api.listPlayerAliases(playerId).then(r => setAliases(r?.aliases || [])).catch(() => setAliases([]))
  }, [api, playerId])

  useEffect(() => { refresh() }, [refresh])

  async function add() {
    if (!draft.alias_form) return
    try {
      await api.createPlayerAlias(playerId, draft)
      setDraft({ alias_form: '', alias_kind: 'nickname', confidence: 1.0 })
      setAdding(false)
      refresh()
    } catch (e) { alert(`Failed: ${e?.message || e}`) }
  }

  async function del(alias_form) {
    if (!confirm(`Delete alias "${alias_form}"?`)) return
    try { await api.deletePlayerAlias(playerId, alias_form); refresh() }
    catch (e) { alert(`Failed: ${e?.message || e}`) }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Aliases</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setAdding(v => !v)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 6px', background: 'transparent', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 3, fontSize: 10, cursor: 'pointer' }}>
          <Plus size={10} /> add
        </button>
      </div>
      {adding && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 6, marginBottom: 6 }}>
          <input placeholder='e.g. "K-Train" or "Kazerski"' value={draft.alias_form} onChange={e => setDraft({ ...draft, alias_form: e.target.value })} style={inpXs} />
          <select value={draft.alias_kind} onChange={e => setDraft({ ...draft, alias_kind: e.target.value })} style={inpXs}>
            <option value="nickname">nickname</option>
            <option value="phonetic">phonetic</option>
            <option value="jersey">jersey</option>
            <option value="stt_mishearing">stt_mishearing</option>
          </select>
          <input type="number" step="0.05" min="0" max="1" value={draft.confidence}
            onChange={e => setDraft({ ...draft, confidence: parseFloat(e.target.value) || 1.0 })} style={inpXs} />
          <button onClick={add} style={{ padding: '3px 8px', background: '#2563eb', border: 'none', color: '#fff', borderRadius: 3, fontSize: 11, cursor: 'pointer' }}>Save</button>
        </div>
      )}
      {aliases === null && <Loading small />}
      {aliases && aliases.length === 0 && (
        <div style={{ color: '#888', fontSize: 11, fontStyle: 'italic' }}>No aliases yet.</div>
      )}
      {aliases && aliases.map((a, i) => (
        <div key={`${a.alias_form}|${a.alias_kind}|${i}`} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 11,
        }}>
          <span style={{ color: '#dbe5f5', minWidth: 140 }}>{a.alias_form}</span>
          <span style={{ color: '#7dd3fc', fontSize: 10, padding: '1px 6px', background: 'rgba(125,211,252,0.1)', borderRadius: 10 }}>{a.alias_kind}</span>
          <span style={{ color: '#888', fontFamily: 'monospace' }}>conf {Number(a.confidence).toFixed(2)}</span>
          <span style={{ color: '#888' }}>· {a.proposed_by || 'editor'}</span>
          <div style={{ flex: 1 }} />
          <button onClick={() => del(a.alias_form)}
            style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer' }}>
            <Trash2 size={10} />
          </button>
        </div>
      ))}
    </div>
  )
}

function Loading({ small = false }) {
  return <div style={{ color: '#888', fontSize: small ? 11 : 13 }}><Loader2 size={small ? 11 : 14} className="spin" style={{ display: 'inline', marginRight: 4 }} /> Loading…</div>
}
const inp = { padding: '6px 8px', background: '#020617', border: '1px solid #334155', color: '#dbe5f5', borderRadius: 3, fontSize: 12 }
const inpXs= { padding: '4px 6px', background: '#020617', border: '1px solid #334155', color: '#dbe5f5', borderRadius: 3, fontSize: 11 }
const th = { padding: '6px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600 }
const td = { padding: '6px 10px', color: '#cbd5e1' }
